import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function About() {
  const values = [
    {
      icon: 'ri-leaf-line',
      title: 'Organic Purity',
      description: 'We source only the finest, certified organic Moringa from sustainable farms'
    },
    {
      icon: 'ri-microscope-line',
      title: 'Scientific Validation',
      description: 'Every product is backed by research and tested for quality and potency'
    },
    {
      icon: 'ri-earth-line',
      title: 'Sustainability',
      description: 'Committed to environmentally responsible practices and fair trade'
    },
    {
      icon: 'ri-heart-line',
      title: 'Wellness First',
      description: 'Your health and well-being are at the heart of everything we do'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section 
          className="relative py-24 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Serene%20moringa%20plantation%20at%20golden%20hour%20with%20rows%20of%20healthy%20moringa%20trees%2C%20farmer%20walking%20through%20organic%20farm%2C%20warm%20sunlight%20filtering%20through%20leaves%2C%20peaceful%20agricultural%20landscape%2C%20sustainable%20farming%20concept%20with%20rich%20soil%20and%20lush%20greenery&width=1920&height=800&seq=about-hero&orientation=landscape')`
          }}
        >
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl font-bold text-white mb-6">Our Story</h1>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              A journey from college classrooms to creating India's trusted organic nutrition brand
            </p>
          </div>
        </section>

        {/* Brand Story - Origins */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Ancient Wisdom, Modern Science</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                At NatureIQ, our story begins long before our first product was created. It starts in the 
                classrooms of <strong>Rajarshi Shahu College, Latur</strong>, where three young students—<strong>Dr. Shirish Punde, 
                Amol Supekar, and Kalyan Kapse</strong>—shared not only science textbooks and dreams, but a deep-rooted 
                belief in the power of education, nature, and purposeful living.
              </p>
            </div>
            
            <div className="prose prose-lg max-w-none space-y-6">
              <p className="text-gray-600">
                Years later, life took them on different paths across the world. But the bond they formed 
                and the values they shared never faded. Each brought unique expertise and experience:
              </p>
              
              <div className="bg-emerald-50 rounded-xl p-8 my-8">
                <ul className="space-y-4 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-user-star-line text-emerald-600 text-xl mt-1 mr-3"></i>
                    <span><strong>Dr. Shirish Punde</strong> – A scientist with deep knowledge of plant-based nutrition and research</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-user-star-line text-emerald-600 text-xl mt-1 mr-3"></i>
                    <span><strong>Amol Supekar</strong> – An entrepreneur passionate about sustainable business and wellness</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-user-star-line text-emerald-600 text-xl mt-1 mr-3"></i>
                    <span><strong>Kalyan Kapse</strong> – A strategist committed to making health accessible to all</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why NatureIQ Was Created */}
        <section className="py-20 bg-emerald-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Why NatureIQ Was Created</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                The founders recognized a critical gap in the modern wellness industry
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-arrow-right-down-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">The Problem</h3>
                <p className="text-gray-600 text-center">Modern lifestyles have distanced people from the natural sources of nutrition</p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-flask-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Market Reality</h3>
                <p className="text-gray-600 text-center">Synthetic supplements dominate the market, lacking purity and authenticity</p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-question-line text-2xl text-yellow-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Consumer Confusion</h3>
                <p className="text-gray-600 text-center">Overwhelmed with options but underwhelmed with quality and transparency</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-10 shadow-lg">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-lightbulb-line text-3xl text-emerald-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">The Solution: NatureIQ</h3>
                <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                  India needed a clean, organic, science-backed nutritional brand that truly respects 
                  ancient wisdom and modern science.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Statement */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Powerful Mission</h2>
                <p className="text-xl text-emerald-600 font-semibold mb-6">
                  To bring the world plant-based, organic, and sustainably crafted nutrition—starting 
                  with the miracle superfood, Moringa.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 mr-3"></i>
                    <p className="text-gray-600">Provide the highest quality organic Moringa products backed by science</p>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 mr-3"></i>
                    <p className="text-gray-600">Bridge ancient Ayurvedic wisdom with modern nutritional research</p>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 mr-3"></i>
                    <p className="text-gray-600">Support sustainable farming practices and fair trade</p>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 mr-3"></i>
                    <p className="text-gray-600">Make plant-based nutrition accessible and trustworthy for everyone</p>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 mr-3"></i>
                    <p className="text-gray-600">Educate consumers about the benefits of natural wellness</p>
                  </div>
                </div>

                <p className="text-gray-700 italic border-l-4 border-emerald-600 pl-4">
                  "We believe that true wellness comes from nature's wisdom. Our mission is to make 
                  holistic health simple, accessible, and rooted in purity."
                </p>
              </div>
              
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Three%20professional%20Indian%20entrepreneurs%20standing%20together%20in%20modern%20office%20with%20green%20plants%2C%20confident%20business%20team%20portrait%2C%20warm%20natural%20lighting%2C%20professional%20corporate%20setting%20with%20moringa%20plants%20in%20background%2C%20successful%20startup%20founders%2C%20collaborative%20teamwork%20atmosphere&width=600&height=500&seq=founders-team&orientation=portrait"
                  alt="NatureIQ Founders"
                  className="rounded-2xl shadow-xl object-cover w-full h-96"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-emerald-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                These principles guide everything we do, from sourcing to production to customer care
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="text-center p-6 bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${value.icon} text-2xl text-emerald-600`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Quality & Certifications</h2>
            <p className="text-lg text-gray-700 mb-12">
              Our commitment to excellence is reflected in our certifications and quality standards
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
                  <i className="ri-leaf-line text-3xl text-emerald-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900">Organic Certified</h4>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
                  <i className="ri-test-tube-line text-3xl text-emerald-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900">Lab Tested</h4>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
                  <i className="ri-shield-check-line text-3xl text-emerald-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900">Quality Assured</h4>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
                  <i className="ri-recycle-line text-3xl text-emerald-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900">Sustainable</h4>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
